import {
    CheckCircleOutlined,
    ErrorOutlined,
    NotificationsOutlined,
    PendingOutlined,
    RefreshRounded,
} from '@mui/icons-material';
import PulsingIcon, { pulse } from '../common/PulsingIcon';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Divider,
    keyframes,
    List,
    ListItem,
    Modal,
    Paper,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useRecalculation } from '../../context/RecalculationStatusContext';
import { useIntegrationsStatusContext } from '../../context/IntegrationsStatusContext';
import { useUserService } from '../../services/user.service';
import { QUERY_KEYS } from '../../utils/queryKeys';

// Define the status info type
interface StatusInfo {
    status: string;
    icon: JSX.Element;
    color: string;
    details: string;
}

const StatusIndicator: React.FC = () => {
    const { t } = useTranslation();
    const { currentCtUser } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { status: recalculationStatus } = useRecalculation();
    const { inProgressIntegrations, isSyncing } = useIntegrationsStatusContext();
    const { isLoading: isDashboardDataLoading } = useDashboardData();
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const userService = useUserService();
    const queryClient = useQueryClient();
    const iconSize = isMobile ? '1.25rem' : '1.5rem';

    // Invalidate integrations query when component mounts
    useEffect(() => {
        if (currentCtUser) {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
        }
    }, [queryClient, currentCtUser]);

    // Status info state
    const [statusInfo, setStatusInfo] = useState<StatusInfo>({
        status: t('sidebar.status.allOk'),
        icon: <PulsingIcon IconComponent={PendingOutlined} size={iconSize} />,
        color: theme.palette.info.main,
        details: t('sidebar.status.allDataUpToDate'),
    });

    const { data: notifications } = useQuery({
        queryKey: [QUERY_KEYS.USER_NOTIFICATIONS],
        queryFn: () => {
            const notifications = userService.getNotifications(20);
            userService.notificationsMarkAsRead();
            return notifications;
        },
        enabled: !!currentCtUser,
    });

    // Update status info when dependencies change
    useEffect(() => {
        const getStatusInfo = () => {
            if (recalculationStatus?.run) {
                return {
                    status: t('sidebar.status.processing'),
                    icon: (
                        <PulsingIcon IconComponent={PendingOutlined} size={iconSize} />
                    ),
                    color: theme.palette.info.main,
                    details: "Daten werden neu berechnet...",
                };
            } else if (inProgressIntegrations.length > 0) {
                return {
                    status: t('sidebar.status.processing'),
                    icon: (
                        <RefreshRounded sx={{ 
                            fontSize: iconSize, 
                            animation: `${pulse} 2s infinite ease-in-out, spin 2s linear infinite`,
                            '@keyframes spin': {
                                '0%': {
                                    transform: 'rotate(0deg)',
                                },
                                '100%': {
                                    transform: 'rotate(360deg)',
                                },
                            },
                        }} />
                    ),
                    color: theme.palette.info.main,
                    details:
                        inProgressIntegrations.length > 1
                            ? `Aktualisiere ${inProgressIntegrations[0]} und ${inProgressIntegrations.length - 1} weitere`
                            : `Aktualisiere ${inProgressIntegrations[0]}`,
                };
            } else if (isDashboardDataLoading) {
                return {
                    status: t('sidebar.status.processing'),
                    icon: (
                        <PulsingIcon IconComponent={PendingOutlined} size={iconSize} />
                    ),
                    color: theme.palette.info.main,
                    details: "Daten werden geladen...",
                };
            } else {
                return {
                    status: t('sidebar.status.allOk'),
                    icon: <CheckCircleOutlined sx={{ fontSize: iconSize }} />,
                    color: theme.palette.success.main,
                    details: "Alle Daten sind aktuell",
                };
            }
        };

        setStatusInfo(getStatusInfo());
    }, [
        recalculationStatus,
        inProgressIntegrations,
        isSyncing,
        isDashboardDataLoading,
        theme.palette.info.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        isMobile,
    ]);

    return (
        <>
            <Tooltip title={statusInfo.details} arrow>
                <Box
                    id="status-icon-container"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        width: isMobile ? 32 : 36,
                        height: isMobile ? 32 : 36,
                        borderRadius: '50%',
                        '&:hover': {
                            backgroundColor: (theme) => theme.palette.action.hover,
                        },
                        color: statusInfo.color,
                    }}
                    onClick={() => setStatusModalOpen(true)}
                >
                    {statusInfo.icon}
                </Box>
            </Tooltip>

            <Modal
                open={statusModalOpen}
                onClose={() => setStatusModalOpen(false)}
                aria-labelledby="status-modal-title"
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        backdropFilter: 'blur(5px)'
                    }
                }}
            >
                <Paper
    sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90vw', sm: 380, md: 480 },
        maxWidth: '95vw',
        p: { xs: 2, sm: 3 },
        borderRadius: { xs: 2, sm: 2, md: 3 },
        boxShadow: theme.shadows[10],
        outline: 'none',
        }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                            sx={{
                                mr: 1.5,
                                color: statusInfo.color,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {statusInfo.icon}
                        </Box>
                        <Typography
                            variant={isMobile ? 'subtitle1' : 'h6'}
                            component="h2"
                            id="status-modal-title"
                            sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}
                        >
                            {statusInfo.details}
                        </Typography>
                    </Box>
                    {inProgressIntegrations.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <List dense disablePadding>
                                {inProgressIntegrations.map((integration, index) => (
                                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                                        <RefreshRounded 
                                            fontSize="small" 
                                            sx={{ 
                                                mr: 1, 
                                                color: theme.palette.info.main,
                                                animation: 'spin 2s linear infinite',
                                                '@keyframes spin': {
                                                    '0%': {
                                                        transform: 'rotate(0deg)',
                                                    },
                                                    '100%': {
                                                        transform: 'rotate(360deg)',
                                                    },
                                                },
                                            }} 
                                        />
                                        <Typography variant="body2">{integration}</Typography>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {notifications && notifications.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ my: 1 }} />
                            <List dense disablePadding>
                                {notifications.map((notification, index) => (
                                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                                        {notification.amount === -1 ? (
                                            <>
                                                <ErrorOutlined
                                                    fontSize="small"
                                                    sx={{ mr: 1, color: theme.palette.error.main }}
                                                />
                                                <Typography variant="body2" color="error.main">
                                                    {`${notification.name}-Import fehlgeschlagen`}
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <NotificationsOutlined
                                                    fontSize="small"
                                                    sx={{ mr: 1, color: theme.palette.info.main }}
                                                />
                                                <Typography variant="body2">
                                                    {`${notification.amount} neue ${notification.name}-Transaktionen wurden importiert`}
                                                </Typography>
                                            </>
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </Paper>
            </Modal>
        </>
    );
};

export default StatusIndicator;
